import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Layout from "../../Shared/Layout";
import {
    Container,
    Grid, Table, TableBody, TableCell, TableHead, TableRow,
} from "@material-ui/core";
import "date-fns";
import styled, {css} from "styled-components";
import BackButton from "../../Shared/BackButton";
import Pagination from "@material-ui/lab/Pagination";
import {useThrottle} from "@uidotdev/usehooks";

import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {Trans} from "../../utils/i18n";
import {usePage} from "@inertiajs/inertia-react";
import classNames from "classnames";
import {Inertia} from "@inertiajs/inertia";
import {Tooltip} from "@mui/material";
import QueueFilter from "./QueueFilter";
import {isEqual} from "lodash";
import {QueueRowCompact} from "../../Shared/QueueRowCompact";
import dayjs from "dayjs";
import useTableConfig from "../../config/queues/useTableConfig";
import Visibility from '@material-ui/icons/Visibility';

const Wrapper = styled.div``;

const PaginationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 15px;
`;

const H1 = styled.h1`
    display: inline-block;
    margin-right: 30px;
`;

const BadgeStyled = styled.div`
    display: inline-block;
    background-color: rgba(0,0,0,0.1);
    border-radius: 30px;
    padding: .05rem .8rem;
    height: 25.56px;
    //color: white;
`;

const VisibilityIcon = styled(Visibility)`
    vertical-align: bottom;
`;

const BadgeStyleUl = styled.ul`
    margin: 0;
    padding: 1rem;
`;

const BadgeStyleLi = styled.li`
    margin: 0;
    padding: 0;
`;

export default function QueueList({
                                      results,
                                      pages,
                                      extendedMode,
                                      defaultFilter,
                                  }) {
    const defaultData = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);

        return {
            page: parseInt(searchParams.get("page") || 1),
            orderBy: searchParams.get("orderBy") || defaultFilter.orderBy,
            orderDirection: searchParams.get("orderDirection") || defaultFilter.orderDirection,
            search: searchParams.get("search"),
            requesttype: searchParams.get("requesttype"),
            service: searchParams.get("service"),
            status: searchParams.get("status") ?? [],
            partner: searchParams.get("partner"),
            company: searchParams.get("company"),
            appointmentDate: searchParams.get("appointmentDate"),
            appointmentStatus: searchParams.get("appointmentStatus") ?? [],
            channel: searchParams.get("channel"),
            dateOfService: searchParams.get("dateOfService"),
            dateFrom: searchParams.get("created_at_from"),
            dateTo: searchParams.get("created_at_to"),
        };
    }, []);

    const [filterData, setFilterData] = useState(defaultData);
    const throttledFilterData = useThrottle(filterData, 300);
    const ref = useRef(defaultData);

    useEffect(() => {
        if (isEqual(ref.current, throttledFilterData)) {
            return;
        }
        ref.current = throttledFilterData;
        Inertia.get(
            route("queue.index"),
            {
                ...throttledFilterData,
                service_id: throttledFilterData.service !== "all" ? throttledFilterData.service : undefined,
                partner_id: throttledFilterData.partner !== "all" ? throttledFilterData.partner : undefined,
                company_id: throttledFilterData.company !== "all" ? throttledFilterData.company : undefined,
                dateOfService: throttledFilterData.dateOfService
                    ? dayjs(throttledFilterData.dateOfService).format("YYYY-MM-DD")
                    : null,
                created_at_from: throttledFilterData.dateFrom
                    ? dayjs(throttledFilterData.dateFrom).format("YYYY-MM-DD")
                    : null,
                created_at_to: throttledFilterData.dateTo
                    ? dayjs(throttledFilterData.dateTo).format("YYYY-MM-DD")
                    : null,
            }, {
                replace: true,
                preserveState: true,
                preserveScroll: true,
            },
        );
    }, [throttledFilterData]);

    const updatePage = useCallback((update) => {
        setFilterData(oldValue => ({
            ...oldValue,
            ...update,
        }));
    }, []);
    const columns = useTableConfig();

    const table = useReactTable({
        data: results,
        columns,
        state: {
            sorting: filterData.orderBy ? [{
                id: filterData.orderBy || "created_at",
                desc: filterData.orderDirection === "desc",
            }] : [],
        },
        onSortingChange: (func) => {
            const newSort = func();
            updatePage({
                orderBy: newSort[0]?.id,
                orderDirection: newSort[0]?.desc ? "desc" : "asc",
            });
        },
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
        enableRowSelection: true,
    })

    const {client} = usePage().props;


    return (
        <Wrapper>
            <Container maxWidth="lg">
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={8} style={{marginBottom: 30}}>
                        <BackButton/>
                    </Grid>
                </Grid>

                <QueueFilter
                    updatePage={updatePage}
                    filterData={filterData}
                />

                {!extendedMode && (
                    <div className={'mt-6'}>
                        {results.map((row) => (
                            <QueueRowCompact
                                key={row.order_id + row.number}
                                row={row}
                            />
                        ))}
                    </div>
                )}

                {extendedMode && (
                    <div className="inline-block min-w-full mt-6 py-2 align-middle">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300" style={{
                                textIndent: 0,
                                borderColor: "inherit",
                                borderCollapse: "collapse",
                            }}>
                                <thead className="bg-gray-50">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id} className="bg-gray-50">
                                        {headerGroup.headers.map(header => (
                                            <th key={header.id} scope="col" className={classNames({
                                                "py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6": header.index === 0,
                                                "px-3 py-3.5 text-left text-sm font-semibold text-gray-900": header.index !== 0 && header.index !== headerGroup.headers.length - 1,
                                                "relative py-3.5 pl-3 pr-4 text-sm font-semibold sm:pr-6": header.index === headerGroup.headers.length - 1,
                                            })}>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={classNames({
                                                            'cursor-pointer select-none': header.column.getCanSort(),
                                                        })}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: ' 🔼',
                                                            desc: ' 🔽',
                                                        }[header.column.getIsSorted()] ?? null}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                {table.getRowModel().rows.map(row => {
                                    const rowOrig = row.original;
                                    return [
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map((cell, index) => (
                                                <td key={cell.id}
                                                    className={classNames({
                                                        "py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6": index === 0,
                                                        "px-3 py-4 text-sm text-gray-500": index !== 0 && index !== row.getVisibleCells().length - 1,
                                                        "relative py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6": index === row.getVisibleCells().length - 1,
                                                    })}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>,
                                        row.getIsSelected() ? (
                                                <tr key={row.id + '_expanded'}>
                                                    <td style={{paddingBottom: 0, paddingTop: 0, width: '100%',}}
                                                        colSpan={13}>
                                                        <div className={"p-6"}>
                                                            <Table size="small" aria-label="purchases">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>
                                                                            <Trans i18nKey="queues.product">Product</Trans></TableCell>
                                                                        <TableCell>
                                                                            <Trans i18nKey="queues.serialNumber">Serial
                                                                                number</Trans>
                                                                        </TableCell>
                                                                        {client.key == 'worldpay' || client.key == 'unzer' && (
                                                                            <TableCell><Trans i18nKey="queues.tid">Terminal
                                                                                ID</Trans></TableCell>
                                                                        )}
                                                                        <TableCell align="left"><Trans
                                                                            i18nKey="queues.quantity">Quantity</Trans></TableCell>
                                                                        {client.key !== 'worldpay' && (
                                                                            <TableCell><Trans
                                                                                i18nKey="queues.outgoingProduct">Outgoing
                                                                                Product</Trans></TableCell>
                                                                        )}
                                                                        {client.key !== 'worldpay' && (
                                                                            <TableCell><Trans
                                                                                i18nKey="queues.outgoingSerialnummer">Outgoing
                                                                                Serialnumber</Trans></TableCell>
                                                                        )}
                                                                        {client.key !== 'worldpay' && (
                                                                            <TableCell><Trans
                                                                                i18nKey="queues.outgoingQuantity">Outgoing
                                                                                Quantity</Trans></TableCell>
                                                                        )}
                                                                        {client.key == 'worldpay' || client.key == 'xiaomi' && (
                                                                            <TableCell><Trans i18nKey="queues.reference">Order
                                                                                Line Reference</Trans></TableCell>
                                                                        )}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {rowOrig.products.map((product) => (
                                                                        <TableRow key={product.number}>
                                                                            <TableCell component="th" scope="row">
                                                                                {product.description}
                                                                            </TableCell>
                                                                            <TableCell>{product.serialnumber}</TableCell>
                                                                            {client.key == 'worldpay' || client.key == 'unzer' && (
                                                                                <TableCell>{product.tid}</TableCell>
                                                                            )}
                                                                            <TableCell>{product.quantity}</TableCell>
                                                                            {client.key !== 'worldpay' && (
                                                                                <TableCell>{product.outgoingDescription}</TableCell>
                                                                            )}
                                                                            {client.key !== 'worldpay' && (
                                                                                <TableCell>
                                                                                    {
                                                                                        product.outgoingSerialnumber && (product?.outgoingSerialnumbersList?.length || 1) > 1 &&
                                                                                        <Tooltip title={<>
                                                                                            <Trans
                                                                                                i18nKey="queueDetail.serialnumbersBelongingToTheProducts">
                                                                                                Serialnumbers belonging to
                                                                                                the
                                                                                                products
                                                                                            </Trans>:
                                                                                            <BadgeStyleUl>
                                                                                                {
                                                                                                    product.outgoingSerialnumbersList.map(item =>
                                                                                                        <BadgeStyleLi
                                                                                                            key={item.id}>{item.serialnumber}</BadgeStyleLi>)
                                                                                                }
                                                                                            </BadgeStyleUl>
                                                                                        </>
                                                                                        } placement="top" arrow>
                                                                                            <BadgeStyled>
                                                                                                {product.outgoingSerialnumber}
                                                                                                <VisibilityIcon/>
                                                                                            </BadgeStyled>
                                                                                        </Tooltip>
                                                                                    }
                                                                                    {
                                                                                        product.outgoingSerialnumber && (product?.outgoingSerialnumbersList?.length || 1) <= 1 &&
                                                                                        <BadgeStyled>
                                                                                            {product.outgoingSerialnumber}
                                                                                        </BadgeStyled>
                                                                                    }
                                                                                </TableCell>
                                                                            )}
                                                                            {client.key !== 'worldpay' && (
                                                                                <TableCell>{product.outgoingQuantity}</TableCell>
                                                                            )}
                                                                            {client.key == 'worldpay' || client.key == 'xiaomi' && (
                                                                                <TableCell>{product.reference}</TableCell>
                                                                            )}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                            : null
                                    ];
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <PaginationWrapper>
                    {pages > 1 && (
                        <Pagination
                            count={pages}
                            page={filterData.page}
                            color={"primary"}
                            onChange={(page, s) => {
                                updatePage({
                                    page: s,
                                });
                            }}
                        />
                    )}
                </PaginationWrapper>
            </Container>
        </Wrapper>
    );
}

QueueList.layout = (page) => <Layout children={page}/>;
